/**
 * `Helper_de_Telemetria` — the pure telemetry layer of the Snap Reports
 * frontend.
 *
 * Feature `telemetry-observability`, task 5.2 (Requirements 11.2, 11.4, 11.5,
 * 11.6, 11.7, 11.10, 14.4, 14.6; design component 8). This module is the
 * **single privacy boundary** between application code and Faro: every event,
 * measurement and error is filtered here through a per-event attribute
 * allowlist before any value reaches the SDK.
 *
 * Design contract (kept deliberately pure and deterministic so the property
 * test P1 — task 5.7, fast-check — can prove that no blocked attribute ever
 * escapes):
 * - **No React, no side effects** beyond the delegated Faro send call. Module
 *   load only builds static lookup tables (pure data).
 * - **Allowlist-driven sanitization.** `sanitizeEvent` keeps *only* the keys
 *   declared for the event's `name` in {@link ATTRIBUTE_ALLOWLIST}; every other
 *   key — user input (CPF, telefone, searched URL), report content, tokens,
 *   cookies, auth headers, PII, concrete resource ids — is stripped
 *   (Req 11.4/11.10/14.4/14.6).
 * - **Safe no-op.** When Faro is disabled or not yet initialized (getter
 *   returns `null`), emitters return without sending and never throw, never
 *   breaking the business flow (Req 11.6). They also never attach a
 *   `traceparent` header — that is the tracing instrumentation's concern.
 * - **Catalog gate.** Events whose `name` is not in the `Catálogo_de_Eventos`
 *   are discarded without sending and without throwing (Req 11.5).
 * - **event / measurement / error classification** follows the ADR metric-vs-log
 *   decision (Req 11.7): funnel + navigation → `pushEvent`; durations
 *   (`durationMs`, and the numeric batch-review friction values) →
 *   `pushMeasurement`; errors → `pushError`. Exception: `export_pdf`/
 *   `export_docx`/`ai_summary` also carry a categorical `phase`, which
 *   `pushMeasurement`'s `context` does not preserve on the wire — see
 *   `DUAL_EMIT_NAMES`, which sends those three to both.
 *
 * The Faro instance is sourced through the bootstrap module's `getFaro()` getter
 * (design component 7, `~/telemetry`). This keeps the helper decoupled from the
 * SDK lifecycle and free of direct `@grafana/faro-web-sdk` value imports, so it
 * stays a pure, lazily-satisfied module.
 */

import { matchPath } from "react-router";

import { getFaro } from "~/telemetry";
import { REDES_SOCIAIS_FIELD_LABELS, REPORT_SECTIONS, TRANSLATED_LABELS } from "~/helpers/constants";
import {
  REPORT_TYPE_KEYS,
  ROUTE_PATTERNS,
  type FailureCategory,
  type FieldKey,
  type ReportTypeKey,
  type RoutePattern,
  type SectionKey,
  type TelemetryEvent,
  type TelemetryEventName,
} from "~/types/telemetry";

/**
 * Reverse of `REPORT_SECTIONS` (key → title): resolves a section's
 * render-time `title` back to its stable catalog key (Req 12.7-style pattern,
 * mirroring `resolveRoutePattern` below). Built lazily, on first
 * `resolveSectionKey` call, and cached — not at module load: many test files
 * mock `~/helpers/constants` partially (without `REPORT_SECTIONS`) and only
 * transitively import this module via `emitEvent`, so evaluating this eagerly
 * would crash module load for every one of them, whether or not they ever
 * touch section telemetry.
 */
let titleToSectionKey: Record<string, SectionKey> | null = null;

function getTitleToSectionKey(): Record<string, SectionKey> {
  if (titleToSectionKey === null) {
    titleToSectionKey = Object.fromEntries(
      Object.entries(REPORT_SECTIONS).map(([key, title]) => [title, key]),
    ) as Record<string, SectionKey>;
  }
  return titleToSectionKey;
}

/**
 * Resolves a report section's runtime `title` to its stable
 * `Catálogo_de_Eventos` key, or `"unknown"` when the title doesn't match any
 * entry in `REPORT_SECTIONS` (defensive fallback — Req 12.10: never let an
 * unrecognized title leak through as free text).
 */
export function resolveSectionKey(title: string): SectionKey {
  return getTitleToSectionKey()[title] ?? "unknown";
}

/**
 * Distinct catalog keys of the given sections, in first-seen order. Titles
 * outside the catalog are dropped: "unknown" would not tell which section the
 * report has, so it cannot serve as a denominator.
 */
export function collectPresentSectionKeys(
  sections: ReadonlyArray<{ title?: unknown }>,
): SectionKey[] {
  const keys = new Set<SectionKey>();
  for (const section of sections) {
    if (typeof section?.title !== "string") {
      continue;
    }
    const key = resolveSectionKey(section.title);
    if (key !== "unknown") {
      keys.add(key);
    }
  }
  return [...keys];
}

/** `REPORT_TYPE_KEYS` as a set, for O(1) membership checks. */
const KNOWN_REPORT_TYPES: ReadonlySet<string> = new Set(REPORT_TYPE_KEYS);

/**
 * Resolves a report's runtime `reportType` (from `useReportType()`) to its
 * stable catalog key, or `"unknown"` when it doesn't match a known type —
 * defensive fallback, same pattern as {@link resolveSectionKey}.
 */
export function resolveReportType(type: string | undefined | null): ReportTypeKey {
  if (typeof type !== "string" || !KNOWN_REPORT_TYPES.has(type)) {
    return "unknown";
  }
  return type as ReportTypeKey;
}

/**
 * Reverse of `TRANSLATED_LABELS` ∪ `REDES_SOCIAIS_FIELD_LABELS` (key →
 * display label, uppercased): resolves a rendered field label back to its
 * stable catalog key, across both closed sets — the ~30 generic sections'
 * prop-name dictionary AND redes_sociais's own per-network field-label
 * catalog. Built lazily and cached, same reason as
 * {@link getTitleToSectionKey}.
 */
let labelToFieldKey: Record<string, FieldKey> | null = null;

function getLabelToFieldKey(): Record<string, FieldKey> {
  if (labelToFieldKey === null) {
    const entries = [
      ...Object.entries(TRANSLATED_LABELS),
      ...Object.entries(REDES_SOCIAIS_FIELD_LABELS),
    ];
    labelToFieldKey = Object.fromEntries(
      entries.map(([key, label]) => [(label as string).toUpperCase(), key]),
    ) as Record<string, FieldKey>;
  }
  return labelToFieldKey;
}

/**
 * Resolves a rendered field label to its stable `Catálogo_de_Eventos` key, or
 * `"unknown"` when the label doesn't match `translate_prop_to_label.json` or
 * `redes_sociais_field_labels.json` — an untranslated/unmapped backend field
 * name, a whole-entry block header (e.g. "TELEFONE 1"), or no label at all
 * (defensive — Req 12.10-style pattern, mirrors {@link resolveSectionKey}).
 * Comparison is case-insensitive since call sites render labels uppercased.
 */
export function resolveFieldKey(label: string | undefined): FieldKey {
  if (label === undefined) {
    return "unknown";
  }
  return getLabelToFieldKey()[label.toUpperCase()] ?? "unknown";
}

/**
 * Matchers built from the `Catálogo_de_Eventos` route patterns (Req 12.7,
 * 13.5). `matchAgainst` normalizes the catalog's bare `"report/:type/:id"`
 * entry to an absolute path for `matchPath`, while {@link resolveRoutePattern}
 * returns the catalog string verbatim (no leading-slash drift). Shared by the
 * navigation instrumentation (task 5.16, `Router.tsx`) and the error-reporting
 * instrumentation (task 5.18, `errorHandling.helper.ts`) so both attribute the
 * current page to the same closed set of patterns.
 */
const ROUTE_MATCHERS: ReadonlyArray<{
  pattern: RoutePattern;
  matchAgainst: string;
}> = ROUTE_PATTERNS.filter((pattern) => pattern !== "unknown").map(
  (pattern) => ({
    pattern,
    matchAgainst: pattern.startsWith("/") ? pattern : `/${pattern}`,
  }),
);

/**
 * Resolves `pathname` to its catalog route **pattern**, or `"unknown"` when no
 * pattern matches (Req 12.7, 13.3). Never returns a concrete URL/id.
 */
export function resolveRoutePattern(pathname: string): RoutePattern {
  for (const { pattern, matchAgainst } of ROUTE_MATCHERS) {
    if (matchPath({ path: matchAgainst, end: true }, pathname)) {
      return pattern;
    }
  }
  return "unknown";
}

/**
 * The only value types a sanitized attribute may hold. The
 * `Catálogo_de_Eventos` restricts every attribute to a categorical string
 * (mode, step, route pattern, phase, failure category, flow tag) or a numeric
 * counter/duration; anything else is dropped as a smuggling vector.
 */
export type AttributeValue = string | number;

/**
 * A telemetry event reduced to its stable name plus the subset of attributes
 * permitted for that name. This is the SDK-agnostic output of the privacy
 * boundary; emitters translate it into the concrete Faro call.
 */
export interface SanitizedEvent {
  readonly name: TelemetryEventName;
  readonly attributes: Readonly<Record<string, AttributeValue>>;
}

/**
 * The closed set of fields an error signal may carry to telemetry (Req 13.3):
 * error type, HTTP status, route **pattern** (never a concrete URL/id) and the
 * operation name. Anything outside these fields — request/response bodies,
 * searched values, tokens, cookies, auth headers, report content — is excluded
 * by construction (see {@link emitError}).
 */
export interface SanitizedError {
  readonly errorType: string;
  readonly operation: string;
  readonly routePattern?: RoutePattern;
  readonly statusCode?: number;
}

/**
 * Per-event attribute allowlist, keyed by every `Catálogo_de_Eventos` name.
 *
 * Using `Record<TelemetryEventName, ...>` makes the catalog the single source
 * of truth: adding a new event to the union fails compilation here until its
 * allowlist is declared, so an event can never ship without an explicit,
 * reviewed set of permitted attributes. The `name` discriminant itself is
 * always retained and is intentionally absent from these sets.
 */
const ATTRIBUTE_ALLOWLIST: Readonly<
  Record<TelemetryEventName, ReadonlySet<string>>
> = {
  report_funnel_open: new Set(["mode", "batchSize"]),
  report_funnel_input_started: new Set(["mode"]),
  report_funnel_advance: new Set(["step", "mode", "batchSize"]),
  report_funnel_back: new Set(["step", "mode"]),
  report_funnel_cancel: new Set(["step", "mode"]),
  report_funnel_submit: new Set(["mode", "batchSize"]),
  report_funnel_success: new Set(["mode", "batchSize"]),
  report_funnel_error: new Set(["mode", "failureCategory"]),
  report_funnel_abandon: new Set(["step", "mode"]),
  batch_review_friction: new Set(["invalidRate", "removedCount", "durationMs"]),
  navigation: new Set(["routePattern"]),
  export_pdf: new Set(["phase", "durationMs"]),
  export_docx: new Set(["phase", "durationMs"]),
  ai_summary: new Set(["phase", "durationMs"]),
  perceived_latency: new Set(["flow", "durationMs"]),
  ui_theme_changed: new Set(["theme"]),
  ui_theme_observed: new Set(["theme"]),
  report_section_toggled: new Set(["action", "section", "reportType"]),
  report_section_present: new Set(["section", "reportType"]),
  report_process_detail_opened: new Set([]),
  report_created_from_field_shortcut: new Set(["section"]),
  report_item_deleted: new Set(["section", "field", "reportType"]),
  report_field_focused: new Set(["section", "field", "reportType"]),
  folder_merge: new Set(["phase"]),
  folder_moved: new Set(["phase"]),
  report_list_search: new Set(["resultCount"]),
  report_list_search_to_creation: new Set([]),
};

/**
 * For events classified as measurements, the numeric attribute keys that become
 * measurement `values`. Any remaining allowlisted attribute is carried as
 * categorical `context`. Events absent from this map (funnel steps, navigation)
 * are emitted as discrete events instead (Req 11.7; design ADR/D8).
 */
const MEASUREMENT_VALUE_KEYS: Readonly<Record<string, readonly string[]>> = {
  perceived_latency: ["durationMs"],
  batch_review_friction: ["invalidRate", "removedCount", "durationMs"],
  export_pdf: ["durationMs"],
  export_docx: ["durationMs"],
  ai_summary: ["durationMs"],
};

/**
 * Names whose Catálogo_de_Eventos shape mixes a categorical attribute
 * (`phase`) with an optional numeric one (`durationMs`). Routing these
 * through `pushMeasurement` alone silently drops `phase`: Faro's measurement
 * `context` is not serialized into the Loki line the way `pushEvent`
 * attributes are (confirmed live 2026-09-03 — an `export_pdf` "success"
 * measurement carries `durationMs` but no `phase` at all). These three emit
 * BOTH: `pushMeasurement` keeps `durationMs` available for percentile
 * analysis, `pushEvent` keeps `phase` queryable as a discrete event
 * (start/success/error), the way every dashboard filtering on it expects.
 */
const DUAL_EMIT_NAMES: ReadonlySet<string> = new Set([
  "export_pdf",
  "export_docx",
  "ai_summary",
]);

/** Shared empty allowlist for defensive lookups; never mutated. */
const EMPTY_ALLOWLIST: ReadonlySet<string> = new Set<string>();

/** Allowlist for the fields of a {@link SanitizedError} carried as context. */
const ERROR_CONTEXT_ALLOWLIST: ReadonlySet<string> = new Set([
  "operation",
  "routePattern",
  "statusCode",
]);

/**
 * Returns the allowlist for a catalog event name using an own-property check,
 * so inherited keys (`toString`, `constructor`, `__proto__`, …) never resolve
 * to a real allowlist. Returns `undefined` for any non-catalog name.
 */
function getAllowedKeys(name: string): ReadonlySet<string> | undefined {
  return Object.prototype.hasOwnProperty.call(ATTRIBUTE_ALLOWLIST, name)
    ? ATTRIBUTE_ALLOWLIST[name as TelemetryEventName]
    : undefined;
}

/**
 * True only when `event` is a well-formed object whose `name` matches the
 * `Catálogo_de_Eventos`. Used to discard out-of-catalog events (Req 11.5).
 */
function isCatalogEvent(event: unknown): event is TelemetryEvent {
  if (typeof event !== "object" || event === null) {
    return false;
  }
  const name: unknown = (event as { name?: unknown }).name;
  return typeof name === "string" && getAllowedKeys(name) !== undefined;
}

/**
 * The core privacy primitive: copy across only keys present in `allowed` whose
 * value is a finite number or a string. Every other key — and any non-primitive
 * or non-finite value under an allowed key — is dropped. Reads own-enumerable
 * keys only, so prototype-pollution keys cannot leak through.
 */
function pickAllowedAttributes(
  source: Readonly<Record<string, unknown>>,
  allowed: ReadonlySet<string>,
): Record<string, AttributeValue> {
  const output: Record<string, AttributeValue> = {};
  for (const key of Object.keys(source)) {
    if (key === "name" || !allowed.has(key)) {
      continue;
    }
    const value = source[key];
    if (typeof value === "string") {
      output[key] = value;
    } else if (typeof value === "number" && Number.isFinite(value)) {
      output[key] = value;
    }
  }
  return output;
}

/**
 * Sanitizes a telemetry event down to its name plus the allowlisted attributes
 * (Req 11.4/11.10/14.4/14.6). Pure and deterministic: given the same input it
 * returns the same output, contains **exclusively** allowlisted attributes,
 * and preserves permitted values unchanged. Attributes outside the allowlist —
 * including any sensitive value smuggled onto the object at runtime — are
 * removed. Property P1 (task 5.7) is proved against this function.
 */
export function sanitizeEvent(event: TelemetryEvent): SanitizedEvent {
  const source = event as unknown as Record<string, unknown>;
  const allowed = getAllowedKeys(event.name) ?? EMPTY_ALLOWLIST;
  return {
    name: event.name,
    attributes: pickAllowedAttributes(source, allowed),
  };
}

/** Stringifies sanitized attributes for Faro's `pushEvent` (string-valued). */
function toStringAttributes(
  attributes: Readonly<Record<string, AttributeValue>>,
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(attributes)) {
    result[key] = String(value);
  }
  return result;
}

/**
 * Extracts the finite numeric measurement `values` for a sanitized event, or
 * `null` when the event is not a measurement (or carries no numeric value yet,
 * e.g. an export/summary `start` phase before a duration exists).
 */
function extractMeasurementValues(
  event: SanitizedEvent,
): Record<string, number> | null {
  const keys = MEASUREMENT_VALUE_KEYS[event.name];
  if (keys === undefined) {
    return null;
  }
  const values: Record<string, number> = {};
  for (const key of keys) {
    const value = event.attributes[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      values[key] = value;
    }
  }
  return Object.keys(values).length > 0 ? values : null;
}

/**
 * Builds the categorical measurement context from the sanitized attributes that
 * are not numeric measurement values, or `undefined` when none remain.
 */
function buildMeasurementContext(
  event: SanitizedEvent,
  valueKeys: readonly string[],
): Record<string, string> | undefined {
  const context: Record<string, string> = {};
  for (const [key, value] of Object.entries(event.attributes)) {
    if (!valueKeys.includes(key)) {
      context[key] = String(value);
    }
  }
  return Object.keys(context).length > 0 ? context : undefined;
}

/**
 * Emits a catalog event to Faro, classifying it as an event or a measurement
 * per the ADR metric-vs-log decision (Req 11.7). Out-of-catalog events are
 * discarded (Req 11.5) and, when Faro is off/uninitialized, this is a safe
 * no-op (Req 11.6). Never throws: any unexpected failure is swallowed so
 * telemetry can never break the business flow.
 */
export function emitEvent(event: TelemetryEvent): void {
  try {
    if (!isCatalogEvent(event)) {
      return;
    }
    const faro = getFaro();
    if (faro === null) {
      return;
    }
    const sanitized = sanitizeEvent(event);
    const valueKeys = MEASUREMENT_VALUE_KEYS[sanitized.name];
    const values = extractMeasurementValues(sanitized);
    if (valueKeys !== undefined && values !== null) {
      faro.api.pushMeasurement({
        type: sanitized.name,
        values,
        context: buildMeasurementContext(sanitized, valueKeys),
      });
      if (!DUAL_EMIT_NAMES.has(sanitized.name)) {
        return;
      }
    }
    faro.api.pushEvent(sanitized.name, toStringAttributes(sanitized.attributes));
  } catch {
    // Telemetry is best-effort: never propagate an emission failure (Req 11.6).
  }
}

/**
 * Emits an ad-hoc numeric measurement (e.g. a flow duration measured by the
 * `Hook_de_Telemetria`). Numeric-only by contract, so values cannot carry
 * PII/report content. Non-finite values are dropped; safe no-op when Faro is
 * off/uninitialized (Req 11.6); never throws.
 */
export function emitMeasurement(
  name: string,
  values: Record<string, number>,
): void {
  try {
    const faro = getFaro();
    if (faro === null) {
      return;
    }
    const finiteValues: Record<string, number> = {};
    for (const [key, value] of Object.entries(values)) {
      if (typeof value === "number" && Number.isFinite(value)) {
        finiteValues[key] = value;
      }
    }
    if (Object.keys(finiteValues).length === 0) {
      return;
    }
    faro.api.pushMeasurement({ type: name, values: finiteValues });
  } catch {
    // Telemetry is best-effort: never propagate an emission failure (Req 11.6).
  }
}

/**
 * Classifies an error/HTTP-response pair into a coarse, non-sensitive
 * {@link FailureCategory} (Req 12.12) — never the error message itself, which
 * may carry user/API-provided text. Shared classifier so funnel error events
 * stay consistent regardless of the call site.
 */
export function classifyFailureCategory(error: unknown): FailureCategory {
  const err = error as {
    code?: string;
    message?: string;
    response?: { status?: number };
    status?: number;
    statusCode?: number;
  } | null;
  if (err === null || typeof err !== "object") {
    return "unknown";
  }
  if (err.code === "ERR_NETWORK" || (err.response === undefined && err.status === undefined)) {
    return "network";
  }
  if (
    err.code === "ECONNABORTED" ||
    (typeof err.message === "string" && err.message.toLowerCase().includes("timeout"))
  ) {
    return "timeout";
  }
  const statusCode = err.response?.status ?? err.status ?? err.statusCode;
  if (statusCode === 400 || statusCode === 422) {
    return "validation";
  }
  if (typeof statusCode === "number" && statusCode >= 500) {
    return "server";
  }
  if (typeof statusCode === "number" && statusCode >= 400) {
    return "client";
  }
  return "unknown";
}

/**
 * Emits a sanitized error to Faro (Req 13.3/13.10/14.6). Only the closed set of
 * {@link SanitizedError} fields is read and forwarded — the error `type` plus
 * a context of `operation`, `routePattern` and `statusCode` — so no
 * request/response body, searched value, token, cookie, auth header or report
 * content can reach telemetry even if present on the source object. Safe no-op
 * when Faro is off/uninitialized (Req 11.6); never throws (Req 13.6).
 */
export function emitError(error: SanitizedError): void {
  try {
    const faro = getFaro();
    if (faro === null) {
      return;
    }
    const errorType =
      typeof error.errorType === "string" && error.errorType.length > 0
        ? error.errorType
        : "UnknownError";
    const context = toStringAttributes(
      pickAllowedAttributes(
        error as unknown as Record<string, unknown>,
        ERROR_CONTEXT_ALLOWLIST,
      ),
    );
    const reported = new Error(errorType);
    reported.name = errorType;
    faro.api.pushError(reported, { type: errorType, context });
  } catch {
    // Telemetry is best-effort: never propagate an emission failure (Req 13.6).
  }
}
